// Game modes.
//
// A mode is a bag of overrides on top of the base tuning in config.js, plus
// whatever the menu needs to draw a card. Nothing here invents numbers: each
// override points at a constant, so tuning still lives in one place.
//
// The systems that only exist in the modern game — specials, antics, the
// passing sky, achievements — are all gated on flags in `state.mods`. That is
// what makes CLASSIC possible without a second copy of the game.

import { state } from './state.js';
import {
  CRATER_RADIUS,
  CRATER_SCALE_DEMOLITION,
  DEBRIS_SCALE_DEMOLITION,
  GRAVITY,
  GRAVITY_LUNAR,
  WIND_MAX,
} from './config.js';

/**
 * The full set of things a mode can change. Every flag defaults to the modern
 * game, so a mode only has to list what it makes different.
 */
const BASE = {
  /** Special attacks, and the button that spends them. */
  specials: true,
  /** Gorillas scratching, beating their chests and nodding off. */
  antics: true,
  /** Shooting stars, the station, the rocket, the banner and the president. */
  sky: true,
  /** Whether anything is recorded towards achievements. */
  achievements: true,
  gravity: GRAVITY,
  craterScale: 1,
  debrisScale: 1,
  windMax: WIND_MAX,
  /** Wind that changes strength mid-flight. */
  gusts: false,
  /** 'rain', 'snow' or null. */
  precipitation: null,
  /** The skyline itself moves, and the gorillas go with it. See advanceQuake(). */
  quake: false,
};

/**
 * The rules of the duel, in the order the cards are laid out on the setup screen: the game as it
 * is, the game as it was, and then the three that bend it. Who is playing is NOT one of these —
 * that is a separate choice on the setup screen, because "one player" and "two players" would
 * otherwise be two ways of saying the same thing.
 *
 * This order is the one you see and nothing else. The order a mode is written into a match code is
 * a wire format and lives in `matchcode.js`, so the cards can be rearranged without renaming the
 * modes inside codes that are already out there.
 */
export const MODES = [
  {
    id: 'modern',
    icon: '🍌',
    mods: {},
  },
  {
    id: 'classic',
    icon: '🦍',
    mods: { specials: false, antics: false, sky: false, achievements: false },
  },
  {
    id: 'quake',
    icon: '🏚️',
    mods: { quake: true },
  },
  {
    id: 'demolition',
    icon: '💥',
    mods: { craterScale: CRATER_SCALE_DEMOLITION, debrisScale: DEBRIS_SCALE_DEMOLITION },
  },
  {
    id: 'lunar',
    icon: '🪐',
    mods: { gravity: GRAVITY_LUNAR },
  },
  {
    id: 'weather',
    icon: '🌪️',
    mods: { gusts: true, precipitation: 'rain' },
  },
];

export const DEFAULT_MODE = 'modern';

/**
 * The original game, and the only mode that is it. Everything else is the modern
 * game wearing a different set of rules.
 *
 * Asked by id rather than inferred from the settings, and the difference is not
 * academic: `storm` turns the specials off too, so gating the original's beeps on
 * `!mods().specials` handed the storm mode the original's fanfare as well. Two
 * modes can share a setting; only one of them is the original. A count of the
 * oscillators built at the start of a round is what caught it.
 */
export function isClassicMode() {
  // The descriptor, not the id: activeMode() hands back the whole card, so asking
  // it whether it is the string 'classic' is a question that is always answered no.
  return activeMode().id === 'classic';
}

/**
 * The resolved settings for the current match, created on first use so that
 * anything reading it before a mode has been chosen still gets the modern game
 * rather than a crash.
 */
export function mods() {
  if (!state.mods) state.mods = { ...BASE };
  return state.mods;
}

export function activeMode() {
  return MODES.find(m => m.id === state.mode) || MODES.find(m => m.id === DEFAULT_MODE);
}

/**
 * Switches mode. Returns false for cards that are not modes, so the menu can
 * send actions and the not-built-yet entry down their own paths.
 */
/**
 * Switches mode. Returns false for an id this build does not have, so a match
 * code from an older version cannot half-apply itself.
 */
export function applyMode(id) {
  const entry = MODES.find(m => m.id === id);
  if (!entry) return false;

  state.mode = id;
  state.mods = { ...BASE, ...(entry.mods || {}) };
  return true;
}
